import { Material, ResolutionType } from '@/app/entities/Material'
import { fetchStringToFile } from '@/app/utils/fetchStringToFile'
import {
  endOfWeek,
  format,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isThisMonth,
  isToday,
  isYesterday,
  setWeek,
  startOfWeek,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  LucideProps,
  // RectangleHorizontal,
  RectangleVertical,
  Square,
} from 'lucide-react'
import { extractFileExtension } from '../ClassDetails/components/helpers'
import { MaterialFormValues } from '../hooks/useMaterialForm'
import { DateRange } from 'react-day-picker'

export const getMaterialImages = (material: Material) => {
  return [
    ...material.feedImages,
    ...material.storyImages,
    ...material.youtubeImages,
  ]
}

export const materialIsClosed = (material: Material) => {
  if (material.dateLiberation) {
    return new Date(material.dateLiberation) >= new Date()
  }

  return false
}

export const groupMaterialsByWeek = (materials: Material[]) => {
  const grouped = materials.reduce(
    (acc, material) => {
      const date = Number(
        format(new Date(material?.dateLiberation), 'w', {
          locale: ptBR,
        }),
      )

      acc[date] = acc[date] || []
      acc[date].push(material)
      return acc
    },
    {} as Record<number, Material[]>,
  )

  return grouped
}

interface MaterialProps {
  hasFeed: boolean
  hasStory: boolean
  hasYoutube: boolean
}

export const getMaterialProps = (material: Material): MaterialProps => {
  return {
    hasFeed: material?.feedImages?.length > 0,
    hasStory: material?.storyImages?.length > 0,
    hasYoutube: material?.youtubeImages?.length > 0,
  }
}
export const parseMaterialToFormValues = async (
  material: Material,
): Promise<MaterialFormValues> => {
  try {
    const liberationDate = new Date(material?.dateLiberation)
    const liberationTime = format(material?.dateLiberation, 'HH:mm')

    const fetchImageFile = async (image: string) => {
      try {
        return await fetchStringToFile(image, extractFileExtension(image))
      } catch (error) {
        console.error(`Erro ao processar imagem: ${image}`, error)
        return null
      }
    }

    const feedImagesFilesPromise = Promise.all(
      material?.feedImages.map(fetchImageFile),
    )

    const storyImagesFilesPromise = Promise.all(
      material?.storyImages.map(fetchImageFile),
    )

    const youtubeImagesFilesPromise = Promise.all(
      material?.youtubeImages.map(fetchImageFile),
    )

    const [feedImagesFiles, storyImagesFiles, youtubeImagesFiles] =
      await Promise.all([
        feedImagesFilesPromise,
        storyImagesFilesPromise,
        youtubeImagesFilesPromise,
      ])

    return {
      _id: material?._id,
      name: material?.name,
      description: material?.description,
      liberationDate,
      liberationTime,
      feedImagesFiles: feedImagesFiles.filter(Boolean),
      storyImagesFiles: storyImagesFiles.filter(Boolean),
      youtubeImagesFiles: youtubeImagesFiles.filter(Boolean),
      isSellersApp: material?.isSellersApp || false,
      isTeamsApp: material?.isTeamsApp || false,
    } as MaterialFormValues
  } catch (error) {
    console.error(
      'Erro ao converter material para valores do formulário:',
      error,
    )
    throw new Error('Não foi possível processar os dados do material.')
  }
}

export const parseFormValuesToMaterialFormData = (
  payload: MaterialFormValues,
) => {
  const liberationDate = new Date(payload?.liberationDate)
  const liberationTime = payload?.liberationTime
  const dateLiberation = `${liberationDate.toISOString().split('T')[0]}T${liberationTime}`

  return {
    name: payload?.name,
    description: payload?.description,
    dateLiberation,
    isSellersApp: payload?.isSellersApp,
    isTeamsApp: payload?.isTeamsApp,
  }
}

export type MATERIAL_RESOLUTION_TYPE = {
  name: string
  type: ResolutionType
  width: number
  height: number
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >
  maxFiles: number | null
}

export const MATERIAL_RESOLUTIONS = [
  {
    name: 'Feed',
    type: ResolutionType.Feed,
    width: 1080,
    height: 1080,
    icon: Square,
    maxFiles: null,
  },
  {
    name: 'Stories',
    type: ResolutionType.Stories,
    width: 1080,
    height: 1920,
    icon: RectangleVertical,
    maxFiles: 1,
  },
  // {
  //   name: 'Miniatura do Youtube',
  //   type: ResolutionType.Youtube,
  //   width: 1080,
  //   height: 720,
  //   icon: RectangleHorizontal,
  //   maxFiles: 1,
  // },
]

export const getLabel = (selectedDate?: DateRange) => {
  if (selectedDate && selectedDate.from && selectedDate.to) {
    const { from, to } = selectedDate

    if (isSameDay(from, to)) {
      if (isYesterday(from)) return 'Ontem'
      if (isToday(from)) return 'Hoje'

      return format(from, 'dd/MM/yyyy')
    }

    if (isFirstDayOfMonth(from) && isLastDayOfMonth(to) && isThisMonth(from))
      return 'Este mês'

    return `De ${format(from, 'dd/MM')} à ${format(to, 'dd/MM')}`
  }

  return 'Selecione uma data'
}

export const getFormattedWeekDate = (week: number, opt?: 'start' | 'end') => {
  const weekDate = setWeek(new Date(), +week)
  if (opt === 'end') return format(endOfWeek(weekDate), 'dd/MM/yyyy')

  return format(startOfWeek(weekDate), 'dd/MM/yyyy')
}
